<template>
  <div class="form__selectPeriodTimeWrapper mt-6">
    <h4 v-if="label" class="mb-0">{{ label }}</h4>

    <v-row ref="periodsWrapper">
      <v-col class="text-center" v-for="period in periods" :key="period.key">
        <div class="form__customCheck">
          <input
            type="radio"
            name="periodSelect"
            :id="`period-${period.key}`"
            :disabled="isPeriodDisabled(period)"
          />
          <label
            :for="`period-${period.key}`"
            :class="`${isPeriodDisabled(period) ? 'invalid' : ''}`"
            @click="setPeriod(period)">
            {{ period.label }}
            <v-icon x-small>mdi-check</v-icon>
          </label>
        </div>
      </v-col>

      <v-col v-if="showInlineCustomTime">
        <v-select
          v-model="selectedTime"
          @change="setCustomTime"
          :disabled="disableAllHours"
          :items="customHours"
          :label="moreOptionsLabel"
          :key="selectKey"
          class="form__customCheckSelect"
          outlined
          dense
        >
          <v-icon v-if="selectedTime" x-small>mdi-check</v-icon>
        </v-select>
      </v-col>
    </v-row>

    <div v-if="showExpandedCustomTime">
      <v-row align="center">
        <v-col>
          <v-select
            v-model="selectedTime"
            @change="setCustomTime"
            :disabled="disableAllHours"
            :items="customHours"
            :label="moreOptionsLabel"
            :key="selectKey"
            class="form__customCheckSelect"
            outlined
            dense
          >
            <v-icon v-if="selectedTime" x-small>mdi-check</v-icon>
          </v-select>
        </v-col>
        <span v-if="endTime" class="grey--text text--lighten-1 caption mx-2">
          {{ $t('appointment.formCreate.labels.to') }}
        </span>
        <v-col>
          <v-select
            v-model="endTime"
            @change="setEndTime"
            @click:clear="clearEndTime"
            :items="endTimeHours"
            :label="endTimeLabel"
            :key="endTimeSelectKey"
            :rules="endTimeRules"
            :clearable="!requiresEndTime"
            class="form__customCheckSelect end-time-select"
            outlined
            dense
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
// third party
import moment from 'moment'
import { mapGetters } from 'vuex';

export default {
  name: 'HPeriodPicker',

  props: {
    label: {
      type: String,
      default: '',
    },

    isToday: {
      type: Boolean,
      default: false,
    },

    selectedDate: {
      type: String,
      default: '',
    },

    showCustomTime: {
      type: Boolean,
      default: true,
    },

    // imobiliária valida por duração e o serviço escolhido tem sobreposição avaliada:
    // sem horário final o backend recusa o agendamento
    requiresEndTime: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isPeriodValid: false,
      selectedPeriod: null,

      periods: [
        {
          key: 'allDay',
          label: this.$t('appointment.formCreate.labels.periods.allDay'),
          start: '08:00:00',
          slotSize: 480,
        },
        {
          key: 'morning',
          label: this.$t('appointment.formCreate.labels.periods.morning'),
          start: '08:00:00',
          slotSize: 240,
        },
        {
          key: 'afternoon',
          label: this.$t('appointment.formCreate.labels.periods.afternoon'),
          start: '12:00:00',
          slotSize: 240,
        },
      ],

      customHours: [],
      selectedTime: null,
      selectKey: 0,
      isTimeInvalid: false,
      disableAllHours: true,
      moreOptionsLabel: this.$t('filters.datePicker.more'),

      endTime: null,
      endTimeHours: [],
      endTimeSelectKey: 0,
    }
  },

  computed: {
    ...mapGetters(['currentRealEstate']),

    selectedDateIsToday() {
      if (this.isToday) return true
      if (!this.selectedDate) return false

      const today = new Date()
      const year = today.getFullYear()
      const month = `${today.getMonth() + 1}`.padStart(2, '0')
      const day = `${today.getDate()}`.padStart(2, '0')

      return `${year}-${month}-${day}` === this.selectedDate
    },

    currentHour() {
      return new Date().getHours()
    },

    hasStartTime() {
      if (!this.selectedDate) return false
      return this.selectedTime !== null
    },

    startTimeString() {
      if (this.selectedTime) return this.selectedTime.trim()
      if (this.selectedPeriod) return this.selectedPeriod.start.substring(0, 5)
      return null
    },

    // requiresEndTime tem precedência: se o backend vai recusar o agendamento sem horário final,
    // esconder o campo por causa do flag deixaria a tela sem saída
    allowsEndTimeSelect() {
      return this.requiresEndTime || this.currentRealEstate.allows_end_time_appointment
    },

    showInlineCustomTime() {
      return this.showCustomTime && (!this.hasStartTime || !this.allowsEndTimeSelect)
    },

    showExpandedCustomTime() {
      return this.showCustomTime && this.hasStartTime && this.allowsEndTimeSelect
    },

    endTimeLabel() {
      const label = this.$t('appointment.formCreate.labels.endTime')
      return this.requiresEndTime ? `${label} *` : label
    },

    endTimeRules() {
      if (!this.requiresEndTime) return []
      return [(value) => !!value || this.$t('appointment.formCreate.errors.endTimeRequired')]
    },
  },

  methods: {
    resetValue() {
      this.selectedPeriod = null
      this.selectedTime = null
      this.resetCustomTime()
      this.resetEndTime()

      if (this.$refs.periodsWrapper) {
        const inputs = this.$refs.periodsWrapper.querySelectorAll('input')

        inputs.forEach((input) => {
          input.nextSibling && input.nextSibling.classList.remove('invalid')
          input.checked = false
        })
      }
    },

    resetCustomSelect() {
      this.selectedPeriod = null
      this.selectedTime = null
      this.resetCustomTime()
      this.resetEndTime()

      if (this.$refs.periodsWrapper) {
        const inputs = this.$refs.periodsWrapper.querySelectorAll('input')

        inputs.forEach((input) => {
          input.checked = false
        })
      }
    },

    resetCustomTime() {
      this.selectKey += 1
    },

    resetEndTime() {
      this.endTime = null
      this.endTimeHours = []
      this.endTimeSelectKey += 1
    },

    isPeriodDisabled(period) {
      if (!this.selectedDateIsToday) return false
      if (this.currentHour >= 18) return true
      if (period.key === 'morning' && this.currentHour >= 12) return true
      return false
    },

    setPeriod(period) {
      if (this.isPeriodDisabled(period)) return
      this.resetCustomTime()
      this.resetEndTime()
      this.selectedPeriod = period
      this.selectedTime = null
      this.$emit('input', period?.start)
      this.$emit('setSlotSize', period?.slotSize, period?.key)
    },

    uncheckPeriodRadios() {
      if (this.$refs.periodsWrapper) {
        const inputs = this.$refs.periodsWrapper.querySelectorAll('input')
        inputs.forEach((input) => {
          input.checked = false
        })
      }
    },

    // time stuff
    setCustomTime() {
      this.isTimeInvalid = false
      this.resetEndTime()
      this.selectedPeriod = null
      this.uncheckPeriodRadios()
      this.$emit('input', this.selectedTime)
      this.$emit('setSlotSize', null, null)
      if (this.selectedTime) {
        this.generateEndTimeHours(this.selectedTime.trim())
      }
    },

    setEndTime() {
      if (!this.endTime || !this.startTimeString) return
      const start = moment(this.startTimeString, 'HH:mm')
      const end = moment(this.endTime.trim(), 'HH:mm')
      const diff = end.diff(start, 'minutes')
      if (diff > 0) {
        this.$emit('setSlotSize', diff, this.selectedPeriod?.key ?? null)
      }
    },

    clearEndTime() {
      this.endTime = null
      this.endTimeSelectKey += 1
      // revert to no slot_size
      this.$emit('setSlotSize', null, this.selectedPeriod?.key ?? null)
    },

    generateCustomHours() {
      const startTime = 7
      const endTime = 22
      const currentHour = moment().format('HH:mm')

      this.customHours = []

      for (let i = 0; i <= 1440; i += 15) {
        let hours = Math.floor(i / 60)
        let minutes = i % 60
        let hourMinute = null

        hours = hours % 24
        minutes < 10 ? (minutes = `0${minutes}`) : minutes
        hourMinute = `${hours}:${minutes}`

        if (
          moment(hourMinute, 'hh:mm').isBetween(
            moment(this.isToday ? currentHour : startTime, 'hh:mm'),
            moment(endTime, 'hh:mm')
          )
        ) {
          this.customHours.push(hours + ':' + minutes + ' ')
        }
      }
    },

    generateEndTimeHours(startTime) {
      const endTime = 22
      this.endTimeHours = []

      for (let i = 0; i <= 1440; i += 15) {
        let hours = Math.floor(i / 60)
        let minutes = i % 60

        hours = hours % 24
        minutes < 10 ? (minutes = `0${minutes}`) : minutes
        const hourMinute = `${hours}:${minutes}`

        if (
          moment(hourMinute, 'HH:mm').isAfter(moment(startTime, 'HH:mm')) &&
          moment(hourMinute, 'HH:mm').isSameOrBefore(moment(endTime, 'hh:mm'))
        ) {
          this.endTimeHours.push(hours + ':' + minutes + ' ')
        }
      }
    },
  },

  watch: {
    selectedDate() {
      this.resetValue()
      this.generateCustomHours()
      this.disableAllHours = !this.selectedDate
    },

    isToday() {
      this.generateCustomHours()
    },
  },

  mounted() {
    this.$nextTick(function() {
      this.generateCustomHours()
    })
  },
}
</script>

<style scoped>
/* Discreet idle state: dashed border, no fill, muted label */
.end-time-select::v-deep fieldset {
  background-color: transparent !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
}

.end-time-select::v-deep .v-label {
  color: rgba(0, 0, 0, 0.3) !important;
  font-weight: 400 !important;
  letter-spacing: normal !important;
}

/* When selected: restore full form__customCheckSelect active look */
.end-time-select.v-input--is-label-active::v-deep fieldset {
  background-color: #009dd9 !important;
  border: transparent !important;
}

.end-time-select.v-input--is-label-active::v-deep .v-select__selection {
  color: #fff !important;
}

/*
 * A mensagem de validação sai do fluxo do pill, ancorada logo abaixo do campo.
 *
 * O .v-input__control destes selects tem altura travada em 36px e flex-wrap: wrap, do visual de
 * pill. Enquanto o campo não tinha rules nada renderizava ali; com a mensagem, o bloco de detalhes
 * não cabia na coluna e quebrava para uma coluna ao lado, onde saía cortado pelo overflow: hidden
 * -- aparecia só "Info" em vermelho à direita do campo. Posicionar em absolute resolve sem relaxar
 * a altura: o pill continua com 36px e a linha de cima não se desloca quando o erro aparece.
 */
.end-time-select::v-deep .v-input__control {
  position: relative;
  overflow: visible;
}

.end-time-select::v-deep .v-text-field__details {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 2px 0 0 0;
  overflow: visible;
}
</style>

